import { ProFormSelect } from "@ant-design/pro-components";
import { useIntl } from "@umijs/max";
import React, { useEffect, useMemo } from "react";
import { type GameItem, getGameList } from "@/services/game";

interface GameSelectProps {
  multiple?: boolean;
  name?: string;
  label?: string;
  placeholder?: string;
  fieldProps?: any;
  showLabel?: boolean;
  forceRefresh?: number;
  /** 仅查询已开通的游戏 */
  onlyOpen?: boolean;
  [key: string]: any;
}

let gameListCache: Array<{
  label: string;
  value: number;
  gameNumber: number;
}> | null = null;
let gameListCacheOnlyOpen: Array<{
  label: string;
  value: number;
  gameNumber: number;
}> | null = null;
let gameListPromise: Promise<void> | null = null;

const fetchGameList = async (
  forceRefresh = false,
  onlyOpen = false,
): Promise<void> => {
  if (forceRefresh) {
    gameListCache = null;
    gameListCacheOnlyOpen = null;
  }

  const cache = onlyOpen ? gameListCacheOnlyOpen : gameListCache;
  if (cache) return;
  if (gameListPromise) {
    await gameListPromise;
    return;
  }

  gameListPromise = (async () => {
    const res = await getGameList({
      page: "1",
      size: "9999",
      onlyOpen: onlyOpen ? true : undefined,
    });
    const list: GameItem[] = res?.list || [];
    const mapped = list.map((item) => ({
      label: item.name,
      value: item.gameNumber,
      gameNumber: item.gameNumber,
    }));
    if (onlyOpen) {
      gameListCacheOnlyOpen = mapped;
    } else {
      gameListCache = mapped;
    }
  })();

  await gameListPromise;
  gameListPromise = null;
};

const GameSelect: React.FC<GameSelectProps> = ({
  multiple = false,
  name = "gameId",
  label,
  placeholder,
  fieldProps,
  showLabel = true,
  forceRefresh = 0,
  onlyOpen = false,
  ...rest
}) => {
  const intl = useIntl();

  useEffect(() => {
    if (forceRefresh > 0) {
      gameListCache = null;
      gameListCacheOnlyOpen = null;
    }
  }, [forceRefresh]);

  const filterOption = useMemo(() => {
    return (input: string, option: { label: string; value: number }) => {
      const searchLower = input.toLowerCase();
      const labelMatch = option.label?.toLowerCase().includes(searchLower);
      const valueMatch = String(option.value).includes(searchLower);
      return labelMatch || valueMatch;
    };
  }, []);

  return (
    <ProFormSelect
      name={name}
      label={
        showLabel
          ? label ||
            intl.formatMessage({
              id: "pages.games.column.name",
              defaultMessage: "游戏名称",
            })
          : undefined
      }
      style={showLabel === false ? { marginBottom: 0 } : undefined}
      fieldProps={{
        mode: multiple ? "multiple" : undefined,
        showSearch: true,
        filterOption,
        placeholder:
          placeholder ||
          intl.formatMessage({
            id: "pages.games.column.keywords.placeholder",
            defaultMessage: "游戏ID/游戏名称",
          }),
        allowClear: true,
        ...fieldProps,
      }}
      request={async () => {
        await fetchGameList(false, onlyOpen);
        return onlyOpen ? gameListCacheOnlyOpen || [] : gameListCache || [];
      }}
      {...rest}
    />
  );
};

export default GameSelect;
